"use client";

import { usePathname } from "next/navigation";
import { useEffect } from "react";

// Per-page Metadata is the canonical way to set <title> in Next 15, but
// 146 of the 160 pages in this app don't export one — so every tab ends
// up reading the root-layout fallback ("vc-engine"). Adding metadata to
// every page is a 146-file edit; instead we derive a useful title from
// the route on the client and overwrite document.title on each
// navigation. Per-page metadata still wins on first paint (SSR-ed in
// <head>), this hook just upgrades the default fallback after hydration.

// Friendly labels keyed by URL segment. Sourced from `i18n/messages/en.json`
// (`nav.items.*` + admin / settings / primer sub-routes); unknown segments
// fall back to title-cased prettification of the slug.
const SEG_LABELS: Record<string, string> = {
  // Top-level operator pages
  brief: "Brief",
  chat: "Chat",
  search: "Search",
  review: "Review",
  capture: "Capture",
  entities: "Entities",
  companies: "Companies",
  people: "People",
  papers: "Papers",
  industries: "Industries",
  theses: "Theses",
  predictions: "Predictions",
  facts: "Facts",
  watchlists: "Watchlists",
  meetings: "Meetings",
  vendors: "Vendors",
  investors: "Investors",
  publishers: "Publishers",
  graph: "Graph",
  primer: "Primers",
  screener: "Screener",
  positions: "Positions",
  funds: "Funds",
  intros: "Intros",
  diligence: "Diligence",
  "research-reports": "Research reports",
  memos: "Memos",
  templates: "Templates",
  notifications: "Notifications",
  // Workflows + agent surfaces
  workflows: "Workflows",
  "agent-runs": "Agent runs",
  analytics: "Analytics",
  eval: "Evaluation",
  evaluation: "Evaluation",
  activity: "Activity",
  learnings: "Learnings",
  "failed-ingests": "Failed ingests",
  improvements: "Improvements",
  merges: "Merges",
  compare: "Compare",
  "concept-audit": "Concept audit",
  lateral: "Lateral",
  // Onboarding + help
  onboarding: "Onboarding",
  help: "Help",
  foundations: "Foundations",
  // Settings
  settings: "Settings",
  account: "Account",
  sensitivity: "Sensitivity",
  retrieval: "Retrieval",
  models: "Models",
  connectors: "Connectors",
  // Admin
  admin: "Admin",
  audit: "Audit",
  cron: "Cron",
  team: "Team",
  training: "Training",
  vault: "Vault",
  "vault-sync": "Vault sync",
  "vault-history": "Vault history",
  slo: "SLOs",
  kek: "KEK rotation",
  "sensitivity-violations": "Sensitivity violations",
  "render-cache": "Render cache",
  health: "Health",
  // Primer leaves
  chips: "Chips",
  foundries: "Foundries",
  processes: "Processes",
  robotics: "Robotics",
  "hardware-design": "Hardware design",
  "robotics-design": "Robotics design",
  "hardware-projects": "Hardware projects",
  architectures: "Architectures",
  structures: "Structures",
  benchmarks: "Benchmarks",
  "ai-consumer-apps": "AI consumer apps",
  "alphafold-mechanics": "AlphaFold mechanics",
  "battery-cells": "Battery cells",
  "battery-materials": "Battery materials",
  "energy-storage-systems": "Energy storage systems",
  "quantum-computing": "Quantum computing",
  "research-programs": "Research programs",
  // Auth
  login: "Sign in",
  bootstrap: "Bootstrap",
  lock: "Locked",
  accept: "Accept invite",
  offline: "Offline",
  sessions: "Sessions",
  // Sub-routes that aren't operator-facing but show up in URLs
  "by-slug": "Lookup",
  new: "New",
};

const SUFFIX = "vc-engine";

// Locales declared in `i18n/routing.ts`. Hard-coded so the title sync
// doesn't pull in next-intl at module load — keeps this hook free of the
// "context from NextIntlClientProvider was not found" failure mode that
// triggers `useSafeTranslations` everywhere else in the chrome.
const LOCALE_PREFIX_RE = /^\/(en|zh-CN)(?=\/|$)/;

function prettify(seg: string): string {
  return seg
    .split(/[-_]/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ")
    .trim();
}

export function titleFromPath(pathname: string): string {
  const stripped = pathname.replace(LOCALE_PREFIX_RE, "");
  const segs = stripped.split("/").filter(Boolean);
  if (segs.length === 0) return `Home · ${SUFFIX}`;
  // Read deepest segment first — `Account · Settings · vc-engine` rather
  // than `Settings · Account · vc-engine`. The deepest path is what the
  // operator's eye lands on in the tab strip.
  const labels = segs.map((s) => SEG_LABELS[s] ?? prettify(s)).reverse();
  return `${labels.join(" · ")} · ${SUFFIX}`;
}

export function useDocumentTitle(): void {
  const pathname = usePathname();
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title = titleFromPath(pathname);
  }, [pathname]);
}
